import React, { useEffect, useState } from "react";
import { useUserProfile } from "../../contexts/UserProfileContext";
import useSessionRecipes from "../../hooks/useSessionRecipes";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import EquipmentPicker from "./EquipmentPicker";
import { DeleteConfirm, Field, SwitchRow } from "./ProfileParts";

const EMPTY = { name: "", equipment: [], isDefault: false };

const listNames = (names) =>
  names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;

/**
 * One equipment set (an equipment preset). Edits save when the sheet closes;
 * a new set saves with Add.
 */
const EquipmentSetSheet = ({ setId, onClose }) => {
  const {
    equipmentPresets = [],
    locationPresets = [],
    addEquipmentPreset,
    updateEquipmentPreset,
    deleteEquipmentPreset,
  } = useUserProfile();
  const { recipes = [] } = useSessionRecipes();
  const open = setId != null;
  const isNew = setId === "new";

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const set = equipmentPresets.find((p) => p.id === setId);
    setForm(
      set
        ? {
            name: set.name || "",
            equipment: Array.isArray(set.equipment) ? [...set.equipment] : [],
            isDefault: Boolean(set.is_default),
          }
        : EMPTY,
    );
    setDirty(false);
    setError(null);
    setConfirming(false);
    // Only when a different set opens; later list updates must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setId]);

  const update = (fields) => {
    setForm((f) => ({ ...f, ...fields }));
    setDirty(true);
    setError(null);
  };

  const invalid = () =>
    !form.name.trim() ? "Give it a name." : form.equipment.length === 0 ? "Pick at least one item." : null;
  const toApi = () => ({
    name: form.name.trim(),
    equipment: form.equipment,
    is_default: form.isDefault,
  });

  const run = async (action, failure) => {
    setBusy(true);
    try {
      await action();
      return true;
    } catch (err) {
      console.error(failure, err);
      setError(`${failure} Check your connection and try again.`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!isNew && dirty) {
      const problem = invalid();
      if (problem) {
        setError(problem);
        return;
      }
      if (!(await run(() => updateEquipmentPreset(setId, toApi()), "Couldn't save."))) return;
    }
    onClose();
  };

  const add = async () => {
    const problem = invalid();
    if (problem) {
      setError(problem);
      return;
    }
    if (await run(() => addEquipmentPreset(toApi()), "Couldn't save.")) onClose();
  };

  const remove = async () => {
    if (await run(() => deleteEquipmentPreset(setId), "Couldn't delete.")) onClose();
  };

  const places = locationPresets.filter((p) => p.equipment_preset_id === setId).map((p) => p.name);
  const recipeCount = recipes.filter((r) => r.equipment_preset_id === setId).length;
  const consequence = [
    places.length ? `${listNames(places)} will have no equipment set.` : null,
    recipeCount
      ? `${recipeCount === 1 ? "A recipe that uses it" : `${recipeCount} recipes that use it`} will use your default equipment.`
      : null,
  ]
    .filter(Boolean)
    .join(" ") || "Nothing else uses it.";

  return (
    <DetailSheet
      open={open}
      onOpenChange={(next) => !next && close()}
      title={isNew ? "Add an equipment set" : form.name || "Equipment set"}
      footer={
        isNew ? (
          <Button type="button" className="h-12 w-full text-base font-semibold" onClick={add} disabled={busy}>
            Add
          </Button>
        ) : undefined
      }
    >
      <Field label="Name" htmlFor="kit-name">
        <Input
          id="kit-name"
          value={form.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="e.g. Home garage"
        />
      </Field>
      <EquipmentPicker
        selectedEquipment={form.equipment}
        onChange={(equipment) => update({ equipment })}
      />
      <SwitchRow
        id="kit-default"
        label="Default set"
        description="Generate starts with this equipment."
        checked={form.isDefault}
        onCheckedChange={(isDefault) => update({ isDefault })}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!isNew && (
        <DeleteConfirm
          name={form.name || "this set"}
          consequence={consequence}
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={remove}
          busy={busy}
        />
      )}
    </DetailSheet>
  );
};

export default EquipmentSetSheet;
